import type { FeatureContent } from "@/lib/features/content";

export const HI_FEATURES: Partial<Record<string, FeatureContent>> = {
  "auto-clip": {
    related: [
      "/use-cases/clippers",
      "/use-cases/podcast-clipping",
      "/calculators/clips-from-one-video",
      "/compare/crayo-vs-opus-clip",
      "/compare/crayo-vs-vizard",
      "/compare/crayo-vs-klap",
    ],
    title: "Auto Clip",
    answer:
      "Auto Clip आपके upload किए गए लंबे video, जैसे podcast, stream या webinar, को लेता है और AI से उसके सबसे दमदार moments ढूंढकर captions वाले छोटे clips में काट देता है। यह उन clippers, podcasters, streamers और agencies के लिए है जो लंबी recordings को बड़ी मात्रा में short-form video में बदलते हैं।",
    description:
      "लंबा video upload करें और AI को best moments ढूंढने दें, फिर उन्हें post-ready clips में कटवाएं।",
    heroPrefix: "लंबे videos को",
    heroHighlight: "viral clips",
    heroSuffix: "में automatically बदलें",
    heroImage: "/features/auto-clip/auto-clip-hero.png",
    buttonText: "अभी video clip करें",
    buttonLink: "/create/auto-clip",
    seoTitle: "AI Auto Clipper: लंबे video से clips बनाएं",
    seoDescription:
      "Podcast, stream या कोई भी लंबा video upload करें और AI से सेकंडों में best moments को viral short-form clips में कटवाएं।",
    faqs: [
      {
        question: "Auto Clip moments कैसे चुनता है?",
        answer:
          "AI पूरे video में hooks ढूंढता है, जैसे stories, punchlines और high-energy हिस्से, और हर clip को score देता है ताकि आप सबसे strong clips पहले post कर सकें।",
      },
      {
        question: "मेरा upload कितना लंबा हो सकता है?",
        answer:
          "लंबा content ही तो मकसद है: podcasts, streams और webinars बढ़िया काम करते हैं। लंबे videos में बस processing में थोड़ा ज़्यादा समय लगता है।",
      },
      {
        question: "क्या clips में subtitles आते हैं?",
        answer:
          "हाँ। हर clip को automatically styled और accurate captions मिलते हैं, और export से पहले आप look बदल सकते हैं।",
      },
      {
        question: "क्या मैं post करने से पहले clips edit कर सकता हूँ?",
        answer:
          "हाँ। किसी भी clip को editor में खोलकर export से पहले trim करें, reframe करें या captions की style बदलें।",
      },
    ],
  },
  "ai-captions": {
    related: [
      "/features/auto-clip",
      "/features/video-editor",
      "/features/clip-cutter",
      "/use-cases/podcast-clipping",
      "/compare/crayo-vs-submagic",
      "/compare/crayo-vs-captions-ai",
    ],
    title: "AI Captions",
    answer:
      "AI Captions clip की आवाज़ को transcribe करके उसे animated, word-timed subtitles में बदल देता है, 32 caption templates और 56 बोली जाने वाली भाषाओं में transcription के साथ। यह उन clippers, podcasters और streamers के लिए है जो बिना एक शब्द type किए हर short को sound-off देखने के लिए captioned चाहते हैं।",
    description:
      "हर clip पर accurate, animated captions। एक template चुनें, keywords highlight करें और editor में style बदलें।",
    heroPrefix: "हर clip में",
    heroHighlight: "AI captions",
    heroSuffix: "automatically जोड़ें",
    heroImage: "/features/ai-captions/ai-captions-hero.png",
    buttonText: "अभी clip पर captions लगाएं",
    buttonLink: "/create/auto-clip",
    seoTitle: "Shorts, Reels और TikTok के लिए AI Captions Generator",
    seoDescription:
      "AI से Shorts, Reels और TikTok के लिए animated captions बनाएं। 32 caption templates, 56 भाषाएँ, keyword highlights और पूरा style control।",
    faqs: [
      {
        question: "Captions कहाँ जुड़ते हैं?",
        answer:
          "Auto Clip और AI Video Editor अपने बनाए हर clip पर captions लगाते हैं। Timeline editor में आप किसी भी video पर AI subtitles चला सकते हैं, या line by line manual subtitles लिख सकते हैं।",
      },
      {
        question: "Crayo कौन सी भाषाएँ transcribe कर सकता है?",
        answer:
          "56 बोली जाने वाली भाषाएँ, जिनमें English, Spanish, Portuguese, French, German, Hindi, Arabic, Japanese और Korean शामिल हैं। Job शुरू करने से पहले आप spoken language चुनते हैं ताकि transcript सही model से बने।",
      },
      {
        question: "क्या मैं captions का look बदल सकता हूँ?",
        answer:
          "हाँ। 32 templates में से एक चुनें, फिर words per line, position, text color, outline, shadow, animation और emphasis adjust करें। Keyword pop ज़रूरी शब्दों को highlight करता है, और बदलाव एक line या सभी lines पर लागू हो सकते हैं।",
      },
      {
        question: "अगर transcript में कोई शब्द गलत आया तो क्या मैं ठीक कर सकता हूँ?",
        answer:
          "हाँ। Clip को editor में खोलें, caption line select करें और text edit करें। Timing और styling वैसी ही रहती है।",
      },
    ],
  },
  "find-viral-moments": {
    related: [
      "/features/auto-clip",
      "/features/clip-cutter",
      "/features/ai-captions",
      "/use-cases/clippers",
      "/calculators/clips-from-one-video",
      "/compare/crayo-vs-opus-clip",
    ],
    title: "Find Viral Moments",
    answer:
      "Find Viral Moments 3 घंटे तक के podcast, stream या interview को scan करता है, सबसे ज़्यादा चलने की संभावना वाले moments को score देता है, और हर एक को अलग clip के तौर पर save करता है। यह उन clippers और podcast teams के लिए है जो पहले shortlist चाहते हैं और editing बाद में।",
    description:
      "लंबा video upload करें, बताएं उसमें क्या है, और ranked moments का set पाएं जिसे आप review, trim और save कर सकते हैं।",
    heroPrefix: "किसी भी लंबे video के",
    heroHighlight: "viral moments",
    heroSuffix: "ढूंढें",
    heroImage: "/features/find-viral-moments/find-viral-moments-hero.png",
    buttonText: "अभी moments ढूंढें",
    buttonLink: "/create/find-moments",
    seoTitle: "AI से लंबे videos में viral moments ढूंढें",
    seoDescription:
      "Podcast, stream या interview upload करें और AI से post करने लायक moments ढूंढें और score करें। एक video से 50 तक clips review, trim और save करें।",
    faqs: [
      {
        question: "Search शुरू होने से पहले मैं क्या control करता हूँ?",
        answer:
          "कितने moments ढूंढने हैं (5 से 50), moment की लंबाई (30, 45, 60 या 90 seconds), spoken language, content किस तरह का है (talking, podcast, interview, action या auto detect) और एक optional focus note, जैसे सिर्फ pricing वाले हिस्से।",
      },
      {
        question: "क्या save होने से पहले मैं moments check कर सकता हूँ?",
        answer:
          "हाँ। Review before saving on करें और moments Clip Cutter में खुलते हैं, जहाँ clip बनने से पहले आप हर एक को keep, skip या trim करते हैं। इसे off रखें तो search खत्म होते ही हर moment आपके Assets में save हो जाता है।",
      },
      {
        question: "Source video कितना लंबा हो सकता है?",
        answer:
          "1 minute से 3 घंटे तक कुछ भी। लंबे sources को scan होने में बस ज़्यादा समय लगता है।",
      },
      {
        question: "इसमें कितने credits लगते हैं?",
        answer:
          "हर मिले moment पर एक workflow credit। जो moments नहीं मिलते उनके credits refund हो जाते हैं, और आप एक साथ तीन searches चला सकते हैं।",
      },
    ],
  },
  "ai-reframe": {
    related: [
      "/features/auto-clip",
      "/features/video-crop",
      "/features/video-editor",
      "/use-cases/podcast-clipping",
      "/compare/crayo-vs-opus-clip",
      "/compare/crayo-vs-vizard",
    ],
    title: "AI Reframe",
    answer:
      "AI Reframe 16:9 recording को 9:16 clip में बदलता है: चेहरे detect करता है, जो बोल रहा है उसे follow करता है, और crop को झटकेदार tracking की जगह smooth, टिके हुए camera moves से हिलाता है। यह उन clippers, podcasters और streamers के लिए है जिन्हें ऐसे vertical clips चाहिए जहाँ speaker हमेशा frame में रहे।",
    description:
      "Face detection और speaker tracking बोलने वाले को center में रखते हैं, जबकि crop widescreen से vertical में बदलता है।",
    heroPrefix: "Video को",
    heroHighlight: "16:9 से 9:16",
    heroSuffix: "में face tracking के साथ reframe करें",
    heroImage: "/features/ai-reframe/ai-reframe-hero.png",
    buttonText: "अभी video reframe करें",
    buttonLink: "/create/auto-clip",
    seoTitle: "AI Reframe: 16:9 से 9:16, face tracking के साथ",
    seoDescription:
      "Widescreen podcasts और streams को vertical clips में बदलें। AI चेहरे ढूंढता है, active speaker को follow करता है और crop को हर move के बीच steady रखता है।",
    faqs: [
      {
        question: "यह कैसे तय करता है कि कहाँ crop करना है?",
        answer:
          "Face detection model shot के हर चेहरे को ढूंढता है और होंठों की हरकत से पता चलता है कि कौन बोल रहा है। Crop active speaker पर center रहता है और नई position पर तभी खिसकता है जब subject dead zone से बाहर निकले, ताकि हर head movement पर frame न हिले।",
      },
      {
        question: "Screen recordings या बिना चेहरे वाले clips का क्या होता है?",
        answer:
          "Planner हर clip को classify करता है। Screen recordings, जिन clips में follow करने के लिए कोई चेहरा नहीं, और जिनमें इकलौता चेहरा कोने में छोटा webcam overlay है, वे blurry crop की जगह पूरा frame रखते हैं।",
      },
      {
        question: "मैं कौन से layouts चुन सकता हूँ?",
        answer:
          "Fullscreen speaker को पूरे vertical frame में रखता है, Streamer blurred backdrop जोड़ता है, Split नीचे दूसरा video stack करता है, और Letterbox बिना crop के पूरा frame रखता है। Output 9:16, 1:1 या 4:5 हो सकता है।",
      },
      {
        question: "क्या मैं framing हाथ से adjust कर सकता हूँ?",
        answer:
          "हाँ। Clip को editor में खोलें, Effects फिर Reframe में जाएं, और camera keyframes edit करें, या पूरे clip के लिए camera को एक speaker पर lock कर दें।",
      },
    ],
  },
  "video-editor": {
    related: [
      "/features/auto-clip",
      "/features/ai-captions",
      "/features/ai-reframe",
      "/features/clip-cutter",
      "/compare/crayo-vs-capcut",
      "/compare/crayo-vs-veed",
    ],
    title: "Video Editor",
    answer:
      "Crayo Video Editor short-form clips के लिए browser में चलने वाला timeline editor है, जिसमें video, caption, text और audio tracks के साथ zooms, transitions, LUTs, speed ramps और camera keyframes हैं। यह हर plan में शामिल है और उन clippers के लिए बना है जो Auto Clip के output को finish करते हैं या अपने uploads को शुरू से edit करते हैं।",
    description:
      "Captions, zooms, transitions, LUTs, speed ramps, cutaways और camera keyframes वाली multi-track timeline। कोई install नहीं, किसी भी plan पर watermark नहीं।",
    heroPrefix: "Short-form clips के लिए बना",
    heroHighlight: "AI video editor",
    heroSuffix: "",
    heroImage: "/features/video-editor/video-editor-hero.png",
    buttonText: "Editor खोलें",
    buttonLink: "/projects",
    seoTitle: "Short-form clips के लिए AI Video Editor",
    seoDescription:
      "Browser में multi-track timeline पर short-form clips edit करें। हर plan में AI captions, zooms, transitions, LUTs, speed ramps और camera keyframes।",
    faqs: [
      {
        question: "Timeline पर क्या होता है?",
        answer:
          "Video, subtitles, text, audio और effects के लिए अलग tracks। Playhead पर clip split करें, शुरुआत या अंत trim करें, reorder करें, और main clip के ऊपर cutaways layer करें, जबकि नीचे का audio automatically duck हो जाता है।",
      },
      {
        question: "कौन से effects शामिल हैं?",
        answer:
          "Timing और easing वाले zooms, fade, slide, whip, punch, glitch और flashes जैसे transitions, LUT color grades, keyframes वाले speed ramps, और समय के साथ crop हिलाने के लिए Camera and reframe panel।",
      },
      {
        question: "Captions कैसे handle होते हैं?",
        answer:
          "Clip पर AI subtitles चलाएं या manual लिखें, फिर 32 templates में से चुनें और words per line, position, animation, emphasis और keyword pop set करें। बदलाव एक line या हर line पर लागू हो सकते हैं।",
      },
      {
        question: "क्या editor हर plan में है?",
        answer:
          "हाँ। Crayo के हर plan में पूरा editor शामिल है। Plans में फर्क बस इतना है कि हर महीने आप कितने minutes का finished video export कर सकते हैं, और effect previews के लिए Chrome या Edge चाहिए।",
      },
    ],
  },
};
